import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../mockData';
import { existingBlogPosts } from '../seo/blogMigration';
import { useSmartLivingSync } from '../hooks/useSmartLivingSync';
import SEO from '../components/SEO';

const BlogListPage = () => {
    const { 
        getAllArticles, 
        clearNewArticlesCount
    } = useSmartLivingSync();

    const [allPosts, setAllPosts] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    
    useEffect(() => {
        const loadAllArticles = () => {
            const combinedArticles = [
                ...blogPosts, 
                ...existingBlogPosts, 
                ...getAllArticles()
            ].sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
            
            setAllPosts(combinedArticles);
        };

        loadAllArticles();

        const handleArticleUpdate = () => {
            loadAllArticles();
            clearNewArticlesCount();
        };

        window.addEventListener('smartliving-articles-updated', handleArticleUpdate);
        
        return () => {
            window.removeEventListener('smartliving-articles-updated', handleArticleUpdate);
        };
    }, [getAllArticles, clearNewArticlesCount]);

    // Unique categories
    const categories = ['All', ...new Set(allPosts.map(post => post.category).filter(Boolean))];
    
    // Filter posts
    const filteredPosts = allPosts.filter(post => {
        const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
        const matchesSearch = searchQuery === '' || 
            post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (post.excerpt && post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesCategory && matchesSearch;
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('ro-RO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    return (
        <>
            <SEO 
                title="Blog & Articole Psihologie - Oana Tenea"
                description="Descoperă articole de specialitate despre anxietate, relații de cuplu, parenting, traumă și dezvoltare personală scrise de psihoterapeutul Oana Tenea, inclusiv articole publicate pe SmartLiving.ro."
                path="/blog"
            />
            
            <div className="bg-stone-50 dark:bg-stone-950 text-stone-800 dark:text-stone-200 py-16 sm:py-24 transition-colors duration-300">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                    
                    {/* Header Banner */}
                    <div className="text-center max-w-3xl mx-auto mb-14 sm:mb-16">
                        <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-terracotta-50 dark:bg-stone-800 text-terracotta dark:text-terracotta-light text-xs font-semibold tracking-wider uppercase mb-4 border border-terracotta-200/60 dark:border-stone-700">
                            Resurse & Perspective Terapeutice
                        </span>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 dark:text-stone-100 tracking-tight">
                            Blog & Articole
                        </h1>
                        <p className="text-stone-600 dark:text-stone-300 text-base sm:text-lg lg:text-xl leading-relaxed mt-6">
                            Reflecții clinice, tehnici practice și ghiduri despre sănătate emoțională, relații conștiente și vindecare.
                        </p>
                        <div className="w-16 h-1 bg-terracotta mx-auto mt-6 rounded-full"></div>
                    </div>

                    {/* SmartLiving Media Banner */}
                    <div className="max-w-4xl mx-auto mb-14">
                        <div className="bg-white dark:bg-stone-900 rounded-3xl p-6 sm:p-8 border border-stone-200/80 dark:border-stone-800 shadow-soft flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 flex items-center justify-center flex-shrink-0 font-bold text-sm">
                                    SL
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="font-bold text-stone-900 dark:text-stone-100 text-base sm:text-lg">
                                            Autor contributor SmartLiving.ro
                                        </h2>
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                            Sincronizat live
                                        </span>
                                    </div>
                                    <p className="text-stone-500 dark:text-stone-400 text-xs sm:text-sm mt-0.5">
                                        Articole periodice despre dinamica relațiilor, gestionarea crizelor și psihologie aplicată.
                                    </p>
                                </div>
                            </div>

                            <a
                                href="https://smartliving.ro/author/oana-tenea/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-5 py-2.5 rounded-full bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-800 dark:text-stone-200 text-xs font-semibold transition-colors flex-shrink-0"
                            >
                                Profil autor SmartLiving
                                <svg className="w-3.5 h-3.5 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* Filter & Search Controls */}
                    <div className="max-w-4xl mx-auto mb-12 space-y-5">
                        
                        {/* Search Input */}
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-stone-400">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Caută articole după cuvinte cheie..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-11 pr-4 py-3.5 rounded-full bg-white dark:bg-stone-900 border border-stone-200/90 dark:border-stone-800 text-stone-800 dark:text-stone-200 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-transparent transition-all shadow-xs text-sm"
                            />
                        </div>

                        {/* Category Pills */}
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                            {categories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
                                        selectedCategory === category
                                            ? 'bg-terracotta text-white shadow-soft'
                                            : 'bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 border border-stone-200/80 dark:border-stone-800'
                                    }`}
                                >
                                    {category === 'All' ? 'Toate Categoriile' : category}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Articles Grid */}
                    {filteredPosts.length === 0 ? (
                        <div className="text-center py-16 bg-white dark:bg-stone-900 rounded-3xl border border-stone-200/80 dark:border-stone-800 max-w-xl mx-auto">
                            <p className="text-stone-500 dark:text-stone-400 mb-4">Nu am găsit articole pentru această căutare.</p>
                            <button
                                onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}
                                className="px-5 py-2 text-xs font-semibold text-terracotta hover:underline"
                            >
                                Resetează filtrele
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredPosts.map((post) => {
                                const isExternalArticle = post.source === 'SmartLiving.ro';
                                const LinkComponent = isExternalArticle ? 'a' : Link;
                                const linkProps = isExternalArticle 
                                    ? { href: post.externalUrl, target: '_blank', rel: 'noopener noreferrer' }
                                    : { to: `/blog/${post.slug || post.newSlug}` };

                                return (
                                    <article 
                                        key={post.id} 
                                        className="group bg-white dark:bg-stone-900 rounded-3xl p-8 border border-stone-200/80 dark:border-stone-800 shadow-soft hover:shadow-warm transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col justify-between"
                                    >
                                        <div>
                                            {/* Meta Top: Category, Source & Reading Time */}
                                            <div className="flex items-center justify-between gap-2 mb-6">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200/60 dark:border-stone-700">
                                                        {post.category}
                                                    </span>
                                                    {isExternalArticle && (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                                                            SmartLiving.ro
                                                        </span>
                                                    )}
                                                </div>

                                                <span className="text-xs text-stone-500 dark:text-stone-400 font-medium flex items-center flex-shrink-0">
                                                    <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                    </svg>
                                                    {post.readingTime} min
                                                </span>
                                            </div>

                                            {/* Title */}
                                            <h3 className="font-display text-xl font-bold text-stone-900 dark:text-stone-100 mb-4 group-hover:text-terracotta transition-colors leading-snug line-clamp-2">
                                                {post.title}
                                            </h3>

                                            {/* Excerpt */}
                                            <p className="text-stone-600 dark:text-stone-400 text-sm leading-relaxed mb-6 line-clamp-3">
                                                {post.excerpt || post.metaDescription}
                                            </p>
                                        </div>

                                        {/* Bottom Action & Date */}
                                        <div className="pt-4 border-t border-stone-100 dark:border-stone-800 flex items-center justify-between">
                                            <span className="text-xs text-stone-400 dark:text-stone-500">
                                                {formatDate(post.publishDate)}
                                            </span>

                                            <LinkComponent 
                                                {...linkProps}
                                                className="inline-flex items-center text-xs font-semibold text-terracotta group-hover:text-terracotta-dark group-hover:translate-x-1 transition-all duration-200"
                                            >
                                                <span>
                                                    {isExternalArticle ? 'Citește pe SmartLiving' : 'Citește articolul'}
                                                </span>
                                                <svg className="w-3.5 h-3.5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                                </svg>
                                            </LinkComponent>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}

                </div>
            </div>
        </>
    );
};

export default BlogListPage;
